import React, { useMemo } from 'react';
import { Modal, Button, Spin, message } from 'antd-v5';
import { connect } from 'react-redux';
import MassMessageForm from '../../../../shared/MassMessageForm';
import { useCreateMassMessage, useMassMessage, useUpdateMassMessage } from '../../../../../hooks/query/useMassMessages';
import { DEFAULT_TEMPLATE_BUTTONS } from '../../../../shared/MassMessageForm/constants';
import { queryMaker } from '../../../../../core/apiRequests';
import './MassMessageModal.scss';

const unwrapMassMessage = data => data?.data || data || null;

const MassMessageModal = ({ open, onClose, eventId, audienceId, audienceCount, editingMessage, auth }) => {
  const createMutation = useCreateMassMessage();
  const updateMutation = useUpdateMassMessage();
  const editingMessageId = open ? editingMessage?.id : undefined;
  const {
    data: fetchedMessage,
    isLoading: isLoadingMessage,
    isError: isMessageError,
  } = useMassMessage({ massMessageId: editingMessageId });

  const templateButtons = useMemo(() => {
    const eventBookingUrl = `${process.env.REACT_APP_OPT_IN_FORM_URL}${queryMaker({
      merchantUuid: auth?.uuid,
      type: 'event',
      eventId,
    })}`;
    return [...DEFAULT_TEMPLATE_BUTTONS, { value: eventBookingUrl, label: 'Event Link', plainText: true }];
  }, [auth?.uuid, eventId]);

  const isEditMode = !!editingMessage;
  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const detailedEditingMessage = unwrapMassMessage(fetchedMessage);
  const editingMessageForForm = isEditMode && detailedEditingMessage?.id ? detailedEditingMessage : undefined;
  const waitingForMessage = isEditMode && isLoadingMessage;

  const handleFormSubmit = async formData => {
    const payload = {
      ...formData,
      eventId,
      audience: { id: audienceId },
    };

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ massMessageId: editingMessage.id, data: payload });
        message.success('Message updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        message.success('Message scheduled successfully');
      }
      onClose();
    } catch (error) {
      console.error('Error saving mass message:', error);
      message.error('Failed to save message. Please try again.');
    }
  };

  let modalBody;
  if (waitingForMessage) {
    modalBody = (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  } else if (isEditMode && (isMessageError || !editingMessageForForm)) {
    modalBody = (
      <div className="mass-message-modal__header">
        <h2 className="mass-message-modal__title">Scheduled message</h2>
        <p>Failed to load this message. Close and try again.</p>
      </div>
    );
  } else {
    modalBody = (
      <MassMessageForm
        audienceCount={audienceCount}
        editingMessage={editingMessageForForm}
        onSubmit={handleFormSubmit}
        isSaving={isSaving}
        showCostAlways
        enableAttachments
        templateButtons={templateButtons}
        renderActions={(handleSubmit, saving) => (
          <div className="mass-message-modal__header">
            <h2 className="mass-message-modal__title">Scheduled message</h2>
            <div className="mass-message-modal__header-actions">
              <Button type="primary" onClick={handleSubmit} loading={saving}>
                Schedule to Send
              </Button>
            </div>
          </div>
        )}
      />
    );
  }

  return (
    <Modal open={open} onCancel={onClose} width={900} footer={null} destroyOnClose className="mass-message-modal">
      {modalBody}
    </Modal>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(MassMessageModal);
